import React from 'react';
import { ScrollView } from 'react-native';
import Typography from '../../../../components/Text/Text';
import { usePolicy } from './usePolicy';
import OwnerSection from '../../components/OwnerSection';
import OperationsSection from '../../components/OperationsSection';
import FilingsSection from '../../components/FilingsSection';
import AdditionalInsuredSection from '../../components/AdditionalInsuredSection';
import DatePickerSection from '../../components/DatePickerSection';
import NavigationButtons from '../../components/NavigationButtons';
import EndorsementsSection from '../../components/EndorsementsSection';
import OwnerDob from 'src/features/application/components/OwnerDob';
import YesNoSelect from '../../components/YesNoSelect';
import { styles } from './PolicyScreen.styles';
import Toast from 'src/components/Toast/Toast';
import InputField from 'src/components/input/input';

const PolicyScreen: React.FC = () => {
  const {
    owner,
    datePicker,
    operations,
    filings,
    endorsements,
    additionalInsured,
    screenNavigation,
    policySaveErrorToast,
    annualMileage,
    isProducer,
    ownership,
    teamDriving,
    pnc,
  } = usePolicy();

  return (
    <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
      <Typography variant="h2">Final details</Typography>

      {/* ── Ownership ─────────────────────────────────────────────── */}
      <OwnerSection owner={owner} />
      <OwnerDob dob={owner.dob} setDob={owner.setDob} />
      {isProducer && (
        <>
          <YesNoSelect
            label="Are there any other owners / business partners?"
            testID="policy-has-other-owners"
            value={ownership.hasOtherOwners}
            onChange={(val) => {
              ownership.setHasOtherOwners(val);
              if (val === 'No') {
                ownership.setRole('');
                ownership.setWillDrive('');
                ownership.setIsLicensed('');
              }
            }}
          />
          {ownership.hasOtherOwners === 'Yes' && (
            <>
              <InputField
                variant="caption"
                label="What is their role?"
                testID="policy-other-owner-role"
                value={ownership.role}
                placeholder="Enter their role"
                onChange={ownership.setRole}
              />
              <YesNoSelect
                label="Will they drive?"
                testID="policy-other-owner-will-drive"
                value={ownership.willDrive}
                onChange={(val) => {
                  ownership.setWillDrive(val);
                  ownership.setIsLicensed('');
                }}
              />
              {ownership.willDrive === 'Yes' && (
                <Typography variant="body1">
                  Please add this person as a driver in the Drivers section.
                </Typography>
              )}
              {ownership.willDrive === 'No' && (
                <YesNoSelect
                  label="Are they licensed to drive the units listed? (e.g. Semi/Class A, Box Truck Class A or C, Hotshot Class R)"
                  testID="policy-other-owner-is-licensed"
                  value={ownership.isLicensed}
                  onChange={ownership.setIsLicensed}
                />
              )}
            </>
          )}
        </>
      )}

      {/* ── Operations ────────────────────────────────────────────── */}
      <OperationsSection operations={operations} />
      {isProducer && (
        <InputField
          variant="caption"
          label="Annual Mileage"
          testID="policy-annual-mileage"
          value={annualMileage.value}
          placeholder="Enter annual mileage"
          onChange={annualMileage.set}
        />
      )}
      {isProducer && (
        <YesNoSelect
          label="Team Driving / Slip Seating?"
          testID="policy-team-driving"
          value={teamDriving.value}
          onChange={teamDriving.set}
        />
      )}

      {/* ── Filings & Endorsements ────────────────────────────────── */}
      <FilingsSection filings={filings} />
      <EndorsementsSection endorsements={endorsements} />
      {isProducer && (
        <>
          <YesNoSelect
            label="Primary Noncontributory Auto Liability Requested?"
            testID="policy-pnc-auto"
            value={pnc.auto}
            onChange={pnc.setAuto}
          />
          <YesNoSelect
            label="Primary Noncontributory Trucking General Liability Requested?"
            testID="policy-pnc-trucking-gl"
            value={pnc.truckingGl}
            onChange={pnc.setTruckingGl}
          />
        </>
      )}

      {/* ── Additional Insured & Policy Start ─────────────────────── */}
      <AdditionalInsuredSection additionalInsured={additionalInsured} />
      <DatePickerSection datePicker={datePicker} />
      {policySaveErrorToast && (
        <Toast
          text={'An error occurred while saving your responses. Please try again.'}
          type={'error'}
          animate={true}
        />
      )}
      <NavigationButtons
        testID={'insurance-policy-final-btn'}
        onBack={screenNavigation.navigateToAddFreight}
        onNext={screenNavigation.navigateToQuote}
        isNextDisabled={!screenNavigation.isNextButtonEnabled}
      />
    </ScrollView>
  );
};

export default PolicyScreen;
